import {
  AfterViewInit,
  Component,
  Injector,
  Input,
  ViewEncapsulation,
  forwardRef,
  inject,
  input,
  output,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  NgControl,
  NgModel,
} from '@angular/forms';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { AutoFocusDirective } from '@shared/directives/auto-focus.directive';
import { KeyboardStatusService } from '@core/providers/keyboard-status/keyboard-status.service';
import { ValidationErrorsInputEntry } from '@shared/components/validation-errors/validation-errors.interface';

@Component({
  selector: 'app-input-with-counter',
  templateUrl: './input-with-counter.component.html',
  styleUrls: ['./input-with-counter.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputWithCounterComponent),
      multi: true,
    },
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MatFormField, MatLabel, MatInput, FormsModule, MatIcon, MatSuffix, AutoFocusDirective],
})
export class InputWithCounterComponent implements AfterViewInit, ControlValueAccessor {
  private keyboardStatusService = inject(KeyboardStatusService);
  private inj = inject(Injector);

  readonly maxLength = input<number | undefined>(undefined);
  readonly minHeight = input<number>();
  readonly placeholder = input<string>('');
  @Input() hint?: string = '';
  readonly label = input<string>('');
  readonly className = input<string>('');
  readonly required = input(false);
  readonly iconClass = input<string>();
  readonly iconName = input<string>();
  readonly errors = input<ValidationErrorsInputEntry[]>();
  readonly autoFocus = input(false);

  @Input() set hostTouched(value: boolean) {
    this.updatedTouched();
  }

  readonly inputRef = viewChild<NgModel>('inputRef');
  readonly blur = output<void>();
  readonly focus = output<void>();

  value: string | undefined;
  counter = 0;
  disabled = false;
  invalid = false;
  hostControl: NgControl | null = null;
  // Function.prototype is a safe reference that will always point to an empty function. This is only initial, to prevent errors
  private propagateChange: Function = Function.prototype;
  private propagateTouched: Function = Function.prototype;

  onTouched: Function = (_: any) => {};

  ngAfterViewInit(): void {
    this.hostControl = this.inj.get(NgControl, null, { optional: true });
    this.updatedTouched();
  }

  onChange(newValue: string) {
    this.value = newValue;
    this.updateCounter(newValue);
    this.propagateChange(newValue);
  }

  updateCounter(newValue: string) {
    this.counter = newValue.length;
  }

  markAsTouched(): void {
    this.keyboardStatusService.setEscModalStatus(true);
    this.propagateTouched();
  }

  writeValue(value: any): void {
    this.value = value ? String(value) : '';
    this.updateCounter(this.value);
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.propagateTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onFocus(): void {
    this.keyboardStatusService.setEscModalStatus(false);
    this.focus.emit();
  }

  onBlur(): void {
    this.markAsTouched();
    this.blur.emit();
  }

  private updatedTouched(): void {
    const inputRef = this.inputRef();
    if (this.hostControl && this.hostControl.touched && inputRef) {
      inputRef.control.markAllAsTouched();
    }
  }
}
